// Input — 일관 폼 컴포넌트. label, error, hint 포함 래퍼 옵션 지원.
// 기존 전역 input 스타일과 공존: ui-input 클래스 추가.

import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** 라벨 텍스트 */
  label?: string;
  /** 오류 메시지 — 있으면 border-danger 적용 */
  error?: string;
  /** 보조 설명 */
  hint?: string;
  /** 좌측 장식 (아이콘 등) — HTML prefix와 충돌 방지를 위해 startAdornment 사용 */
  startAdornment?: ReactNode;
  /** 우측 장식 */
  endAdornment?: ReactNode;
}

/**
 * Locker UI — Input
 * @example
 * <Input label="사용자명" error="필수 항목입니다" />
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, hint, startAdornment, endAdornment, className = '', id, ...rest }, ref) => {
    const inputId = id ?? (label ? `input-${label.replace(/\s+/g, '-')}` : undefined);
    const hasWrapper = startAdornment || endAdornment;

    const inputEl = (
      <input
        ref={ref}
        id={inputId}
        className={[
          'ui-input',
          error ? 'ui-input--error' : '',
          hasWrapper ? 'ui-input--inner' : '',
          className,
        ]
          .filter(Boolean)
          .join(' ')}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={
          [error ? `${inputId}-error` : '', hint ? `${inputId}-hint` : '']
            .filter(Boolean)
            .join(' ') || undefined
        }
        {...rest}
      />
    );

    return (
      <div className="ui-input-wrap">
        {label && (
          <label htmlFor={inputId} className="ui-input-label">
            {label}
          </label>
        )}
        {hasWrapper ? (
          <div className={`ui-input-adornment${error ? ' ui-input-adornment--error' : ''}`}>
            {startAdornment && <span className="ui-input-prefix">{startAdornment}</span>}
            {inputEl}
            {endAdornment && <span className="ui-input-suffix">{endAdornment}</span>}
          </div>
        ) : (
          inputEl
        )}
        {error && (
          <span id={`${inputId}-error`} className="ui-input-error" role="alert">
            {error}
          </span>
        )}
        {hint && !error && (
          <span id={`${inputId}-hint`} className="ui-input-hint">
            {hint}
          </span>
        )}
      </div>
    );
  },
);

Input.displayName = 'Input';
